import {
  AdminUserResponseModel,
  EditHaAdminUserCommand,
  useEditHaAdminUserFunctionRequest,
  useListAdminPermissionsFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { CheckboxGrid, CheckboxGridItem } from "../../../UIPalette/components/CheckboxGrid/CheckboxGrid";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { Form } from "../../../UIPalette/components/Form/Form";
import { ReactComponent as InfoIcon } from "shared/assets/Icons/info.svg";
import { PageHeader } from "../../../UIPalette/components/PageHeader/PageHeader";
import { Roles } from "../../../core/Constants";
import { getServerErrorMessage } from "shared/utils/serverError";
import { haAdminPermissionDependencies } from "../../adminUserPermissions";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { useTranslation } from "react-i18next";
import { Input } from "shared/UI/Input/Input";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

type Inputs = {
  permissions: CheckboxGridItem[];
};

export const EditWWAdmin = () => {
  const { user } = useAdminUserContext();
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();
  const [errorMessage, setErrorMessage] = useState("");
  const { data, error: permissionError } = useListAdminPermissionsFunctionRequest({ role: Roles.HaAdmin });

  const { trigger: editHaAdminUserTrigger } = useEditHaAdminUserFunctionRequest({});
  const navigateHook = useNavigate();
  const {
    control,
    formState: { errors },
    getValues,
    reset,
    trigger,
  } = useForm<Inputs>({
    defaultValues: {
      permissions: [],
    },
  });

  const userData = useLocation().state as AdminUserResponseModel;
  const isEditingSelf = userData.id === user?.id;

  const onSubmit = async () => {
    setErrorMessage("");

    const selectedPermissions = getValues("permissions").filter((p) => p.selected);
    const permissionsIds = selectedPermissions.map(function (v) {
      return v.id;
    });

    const requestBody: EditHaAdminUserCommand = {
      userId: userData.id,
      permissions: permissionsIds,
    };

    try {
      await editHaAdminUserTrigger({ body: requestBody });
      modalNotify("success", t("adminEdit.successTitle"), t("adminEdit.successMessage", { user: userData.email }));
      navigateHook("/usermanagement/wwadmins");
    } catch (error: any) {
      setErrorMessage(getServerErrorMessage(error, t("adminEdit.requestErrorMessage")));
    }
  };

  const cancelCreate = async () => {
    navigateHook("/usermanagement/wwadmins");
  };

  useEffect(() => {
    if (data?.permissions && getValues("permissions").length === 0) {
      reset({
        permissions: data!.permissions.map((perm) => ({
          id: perm.id,
          key: perm.key,
          displayText: t<string>(`adminUser.permissions.${perm.key}`),
          selected: userData.permissions.includes(perm.key),
        })),
      });
    }
  }, [reset, data, getValues, t, userData.permissions]);

  return (
    <div>
      <PageHeader title={t("adminEdit.ww.title")} subtitle={t("adminEdit.ww.subtitle")} />
      <Form
        className="mt-4"
        onSubmit={onSubmit}
        onCancel={cancelCreate}
        validationTrigger={trigger}
        submitButtonText={t("adminEdit.saveButton")}
        disableSubmit={isEditingSelf}
      >
        <div className="w-1/2 mb-4">
          <Input id="emailAddress" aria-label={t("adminEdit.ariaEmail")} type="text" readOnly={true} value={userData.email} />
        </div>

        <Controller
          control={control}
          name="permissions"
          rules={{ validate: () => getValues("permissions").some((p) => p.selected) }}
          render={({ field: { value, onChange } }) => {
            return getValues("permissions").length === 0 ? (
              <></>
            ) : (
              <div>
                <CheckboxGrid
                  items={value}
                  dependencies={haAdminPermissionDependencies}
                  headingText={t("adminEdit.permissions.selectPermissions")}
                  onChange={(state) => onChange(state)}
                  disabled={isEditingSelf}
                />
              </div>
            );
          }}
        />
        {isEditingSelf && (
          <div className="flex gap-2 mt-8 items-center w-fit">
            <InfoIcon className="text-brand w-[2rem] h-[2rem]" />
            <p>{t("adminEdit.ww.editingSelfInfoMessage")}</p>
          </div>
        )}

        {errors.permissions && <ErrorAlert content={t("adminEdit.noPermissionsSelectedError")} />}
        {permissionError && <ErrorAlert content={t("adminEdit.permissions.error")} />}
      </Form>
      <ErrorAlert content={errorMessage} />
    </div>
  );
};
